import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { TrainingSession } from '@/features/training/types';

interface TrainingHeaderProps {
  title: string;
  description: string;
  trainingData?: TrainingSession | null;
  isLoading?: boolean;
}

export function TrainingHeader({
  title,
  description,
  trainingData,
  isLoading = false,
}: TrainingHeaderProps) {
  const hasActiveSession = trainingData;

  // Generate status badge based on training data
  const getStatusBadge = () => {
    if ((!hasActiveSession || !trainingData) && !isLoading) return null;

    const serverDetails = trainingData?.server;

    if (isLoading) {
      return (
        <Badge
          variant="secondary"
          className="gap-x-2 self-center whitespace-nowrap bg-secondary/50 text-muted-foreground text-sm"
        >
          <Spinner size="sm" className="size-4!" />
          Setting up server...
        </Badge>
      );
    } else if (!serverDetails) {
      return (
        <Badge
          variant="destructive"
          className="gap-x-2 self-center whitespace-nowrap bg-destructive/50 text-destructive text-sm"
        >
          <Separator
            orientation="vertical"
            className="!h-3 !w-1 rounded-full bg-destructive"
          />
          Not Reachable
        </Badge>
      );
    } else if (serverDetails.booting) {
      return (
        <Badge
          variant="secondary"
          className="gap-x-2 self-center whitespace-nowrap bg-green/20 text-green hover:bg-green/30 text-sm"
        >
          <Separator
            orientation="vertical"
            className="!h-3 !w-1 rounded-full bg-green"
          />
          Server on
        </Badge>
      );
    } else if (serverDetails.on) {
      return (
        <Badge
          variant="secondary"
          className="gap-x-2 self-center whitespace-nowrap bg-green/20 text-green hover:bg-green/30 text-sm"
        >
          <Separator
            orientation="vertical"
            className="!h-3 !w-1 rounded-full bg-green"
          />
          Server on
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="secondary"
          className="gap-x-2 self-center whitespace-nowrap bg-secondary/50 text-muted-foreground text-sm"
        >
          <Separator
            orientation="vertical"
            className="!h-3 !w-1 rounded-full bg-muted-foreground"
          />
          Server off
        </Badge>
      );
    }
  };

  return (
    <>
      <div className="text-left mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-2xl font-medium text-foreground">{title}</h2>
          {getStatusBadge()}
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl text-pretty">
          {description}
        </p>
      </div>

      <Separator className="my-8" />
    </>
  );
}
